"use client"

import styled from "@emotion/styled"
import { Article, GetArticlesQuery } from "@eurodrive/apollo"
import { extractProperty, getNonUmbracoUrl, Link } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { Button, Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import { Swiper, SwiperSlide } from "swiper/react"
import Container from "./Container"
import { sharedColors } from "./theme/colors"
import { TransitionRight } from "./transitions/TransitionRight"
import { TransitionUp } from "./transitions/TransitionUp"
import { useInViewConfig } from "./transitions/utils"

const StyledLayout = styled.div<{ noBackgroundColor?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 64px 0;
  gap: 40px;
  background-color: ${({ noBackgroundColor }) =>
    noBackgroundColor ? "transparent" : sharedColors.lightGrey};
`

const InnerLayout = styled.div`
  padding-left: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-left: calc((100vw - 1280px) / 2 + 88px);
  }

  ${({ theme }) => theme.breakpoints.up("xl")} {
    padding-left: calc((100vw - 1920px) / 2 + 240px);
  }
`

const StyledButton = styled(Button)<{ target: string }>`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    margin: auto;
    width: 249px;
  }
`

const StyledSwiperSlide = styled(SwiperSlide)`
  width: 271px !important;
  height: 439px !important;
`

const StyledItemLayout = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  border: 1px solid #ece9e9;
  background-color: white;
`

const ImageContainer = styled.div`
  width: 100%;
  height: 195px;
  position: relative;
  flex-shrink: 0;
`

const Content = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`

const CategoryChip = styled.div`
  display: flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  width: fit-content;
  background-color: ${sharedColors.lightGrey};
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledLink = styled(Link)`
  display: flex;
  gap: 8px;
  align-items: center;
  text-decoration: none;
  width: fit-content;
  margin-top: auto;
  :hover {
    /* only bottom border */
    box-shadow: inset 0px -1px 0px ${sharedColors.darkGreen};
  }
  :focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledTitle = styled(Typography)`
  ${({ theme }) => theme.breakpoints.up("md")} {
    text-align: center;
  }
`

const StyledArticleTitle = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledDescription = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default function Articles({
  data,
  articles,
  noBackgroundColor,
}: {
  data: any
  articles: GetArticlesQuery
  noBackgroundColor?: boolean
}) {
  const { ref: inViewRef, inView } = useInView(useInViewConfig)

  // Extract properties from the block data
  const title = extractProperty(data, "title") as string
  const articlesArray =
    articles.components?.edges?.map((el) => {
      const object = el.node
      const properties = object?.namedProperties as Article
      return {
        url: object?.url,
        title: properties?.title?.value,
        image: properties?.image?.mediaItems?.[0]?.url,
        description: properties?.description?.value,
        category: properties?.category?.value,
      }
    }) || []

  const buttonName = extractProperty(data, "button")?.[0]?.name
  const buttonUrl = extractProperty(data, "button")?.[0]?.url
  const buttonExternal =
    extractProperty(data, "button")?.[0]?.type === "EXTERNAL"
  const items = extractProperty(data, "itemsList") || []

  const filteredArticles = articlesArray.filter((item) =>
    items.some((el: any) => el.url === item.url),
  )
  return (
    <StyledLayout ref={inViewRef} noBackgroundColor={noBackgroundColor}>
      <Container paddingMobile>
        <TransitionUp $inView={inView}>
          <StyledTitle variant="h2">{title}</StyledTitle>
        </TransitionUp>
      </Container>
      <InnerLayout>
        <Swiper
          spaceBetween={12}
          slidesPerView="auto"
          style={{
            width: "100%",
            paddingLeft: "20px",
          }}
          pagination={{ clickable: true }}
          role="list"
          aria-label={title}
        >
          {filteredArticles.map((item, index) => (
            <StyledSwiperSlide key={index} role="listitem">
              <TransitionRight $inView={inView}>
                <StyledItemLayout>
                  <ImageContainer>
                    <Image
                      fill
                      src={getUmbracoMediaUrl(item.image || "")}
                      alt={item.title}
                    />
                  </ImageContainer>
                  <Content>
                    <CategoryChip>
                      <Typography variant="body2">{item.category}</Typography>
                    </CategoryChip>
                    <TextBox>
                      <StyledArticleTitle variant="h3">
                        {item.title}
                      </StyledArticleTitle>
                      <StyledDescription variant="body1">
                        {item.description}
                      </StyledDescription>
                    </TextBox>
                    <StyledLink
                      href={getNonUmbracoUrl(item.url || "")}
                      title={item.title}
                    >
                      <Typography
                        variant="body1"
                        color={sharedColors.darkGreen}
                        fontWeight={600}
                      >
                        לקריאת הכתבה
                      </Typography>
                      <Image
                        src={"/icons/left-arrow-alt-green.svg"}
                        alt=""
                        aria-hidden="true"
                        width={24}
                        height={24}
                      />
                    </StyledLink>
                  </Content>
                </StyledItemLayout>
              </TransitionRight>
            </StyledSwiperSlide>
          ))}
        </Swiper>
      </InnerLayout>
      {buttonName && (
        <TransitionUp $inView={inView}>
          <Container paddingMobile>
            <StyledButton
              variant="primary"
              size="large"
              href={getNonUmbracoUrl(buttonUrl)}
              target={buttonExternal ? "_blank" : "_self"}
              LinkComponent={Link}
            >
              {buttonName}
            </StyledButton>
          </Container>
        </TransitionUp>
      )}
    </StyledLayout>
  )
}
